import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Brand, BrandingService } from '@approot/branding';
import {
  AuthPayload,
  AuthService,
  PhoenixEventBusService
} from '@approot/services';
import { isFeatureEnabled, parseJWT, sleep } from '@approot/utilities';
import { retry, timer } from 'rxjs';

type Status =
  | 'PENDING'
  | 'SUCCESS'
  | 'ERROR'
  | 'NEEDS_SETUP'
  | 'UNEXPECTED_SSO_ROLES'
  | 'NO_SSO_ROLES'
  | 'NO_GROUPS'
  | 'PROVISIONING'
  | 'SSO_ROLES_INVALID';

@Component({
  selector: 'app-sso',
  templateUrl: './sso.component.html',
  styleUrls: ['./sso.component.scss']
})
export class SsoComponent implements OnInit, OnDestroy {
  public message = 'Validating...';
  public status: Status = 'PENDING';
  public groups: string[] = [];
  private loginTimeout: any;
  public brand = inject(BrandingService);
  public phxQueue = inject(PhoenixEventBusService);
  public brandLoaded = false;

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  public async ngOnInit() {
    sessionStorage.setItem('isSSO', 'true');

    try {
      await this.authService.currentSession();
      this.setStatus('You are already logged in! Logging out...', 'PENDING');
      this.authService.logout();
    } catch {}

    const { search, clientId } = parseURL(location);
    if (!search.code) {
      this.errorStatus = search.error_description ?? 'No code found';
      if (search) return;
    }
    try {
      // tslint:disable: no-console
      let codes: AuthPayload;
      try {
        console.time('SSO.exchangeCode');
        codes = await this.authService.exchangeCode(search.code, clientId);
      } finally {
        console.timeEnd('SSO.exchangeCode');
      }

      if (codes.error) {
        if (codes.error === 'invalid_grant') {
          this.errorStatus = `Login failed - please reinitiate login via your company's portal`;
          return;
        }

        throw new Error(codes.error);
      }
      this.authService.authPayload = codes;

      await this.signIn();
      this.testRefresh();
    } catch (e) {
      this.errorStatus = 'Login failed';
      console.warn('SSO init - login failed', e.error?.message || e);
    }
  }

  public ngOnDestroy() {
    clearTimeout(this.loginTimeout);
  }

  private async testRefresh() {
    await this.authService.refresh();
  }

  private async signOut(wait: number) {
    await sleep(wait);
    this.authService.logout();
  }

  private async signIn(): Promise<void> {
    await this.federatedSignIn();
  }

  private async federatedSignIn() {
    this.loginTimeout = setTimeout(() => {
      if (this.status === 'PENDING') {
        this.errorStatus =
          'Login failed - please try again or contact support if this error persists';
      }
    }, 20_000);

    return this.authService
      .currentSession()
      .then(async session => {
        clearTimeout(this.loginTimeout);

        const jwt = session.getIdToken().getJwtToken();
        const token = parseJWT(jwt);
        const group = token['cognito:groups']?.[0];
        if (group) {
          console.log('User group:', group);
          this.brand.setBrand(group);
          this.brandLoaded = true;
        }

        const noSSORoles = token['custom:noSSORoles'] === 'true';
        if (noSSORoles) {
          this.setStatus('Login failed', 'NO_SSO_ROLES');
          return null;
        }

        const ssoRolesInValid = token['custom:SSORolesInValid'] === 'true';
        if (ssoRolesInValid) {
          this.setStatus('Login failed', 'SSO_ROLES_INVALID');
          return null;
        }

        try {
          const res = await this.getUserStatus();
          const status: Status = res.isUserSetupInHydra
            ? 'SUCCESS'
            : 'NEEDS_SETUP';

          this.setStatus(
            `You are now logged in to ${this.brand.brandName()}`,
            status
          );

          const delay = isFeatureEnabled('localDev', false) ? 15_000 : 1000;
          setTimeout(() => this.home(), delay);
        } catch (e) {
          this.setStatus('Account provisioning', 'PROVISIONING');
          console.error('getUserStatus', e);
          await this.signOut(3000);
        }
      })
      .catch(async e => {
        this.errorStatus = 'Login failed';
        console.error('federatedSignIn', e);
        await this.signOut(1000);
      });
  }

  public home() {
    this.phxQueue.addEvent({ type: 'access', category: 'login' });
    this.router.navigateByUrl('/');
  }

  private set errorStatus(message: string) {
    this.setStatus(message, 'ERROR');
    clearTimeout(this.loginTimeout);
  }

  private setStatus(message: string, status: Status) {
    this.message = message;
    this.status = status;
  }

  private getUserStatus(): Promise<{ isUserSetupInHydra: boolean }> {
    return new Promise(async (resolve, reject) => {
      this.authService
        .fxPost(
          { task: 'getUserStatus' },
          'get-user-info',
          'task=getUserStatus'
        )
        .pipe(
          retry({
            count: 2,
            delay: (_error, attempt) => {
              const interval = 500;
              const delay = Math.pow(2, attempt - 1) * interval;
              return timer(delay);
            }
          })
        )
        .subscribe({
          next: (res: any) => resolve(res),
          error: e => reject(e)
        });
    });
  }
}

function parseURL(loc: Location): {
  search: Record<string, string>;
  clientId: string;
} {
  const search = loc.search
    .substring(1)
    .split('&')
    .reduce((acc, part) => {
      const [name, value] = part.split('=');
      return { ...acc, [name]: decodeURIComponent(value) };
    }, {});
  const path = loc.pathname.replace(new RegExp('/$'), '');
  const clientId = path.split('/').at(-1);
  return { search, clientId };
}
